'use client';

import { ElementRef, use, createContext } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';

// Radix requires a TooltipProvider ancestor; this context flag lets <Tooltip>
// self-wrap when no provider is present, so callers in trees without one
// (e.g. ConfigurationDialog opened from /account/integrations) don't crash.
// A real ancestor TooltipProvider is still preferred — it shares state across
// sibling tooltips — so the flag suppresses the self-wrap when one exists.
const TooltipProviderContext = createContext(false);

const TooltipProvider = ({
  children,
  ...props
}: ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>) => (
  <TooltipPrimitive.Provider {...props}>
    <TooltipProviderContext value={true}>{children}</TooltipProviderContext>
  </TooltipPrimitive.Provider>
);

const Tooltip = ({
  children,
  ...props
}: ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>) => {
  const hasProvider = use(TooltipProviderContext);
  const root = (
    <TooltipPrimitive.Root {...props}>{children}</TooltipPrimitive.Root>
  );
  return hasProvider ? root : <TooltipProvider>{root}</TooltipProvider>;
};

const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * Renders the tooltip into `document.body` instead of in place.
 *
 * `TooltipContent` below is deliberately NOT portalled by default (changing
 * that would move every tooltip in the app), so a trigger sitting inside an
 * `overflow-hidden` ancestor has its tooltip **clipped away** — it is present
 * in the DOM at full size and simply not painted, which is why it reads as
 * "the tooltip doesn't work" rather than as a layout bug. Wrap `TooltipContent`
 * in this when the trigger lives inside a clipping container (the matter-list
 * row is one: its root carries `overflow-hidden`).
 */
const TooltipPortal = TooltipPrimitive.Portal;

const TooltipContent = ({
  ref,
  className,
  sideOffset = 4,
  ...props
}: ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
  ref?: React.RefObject<ElementRef<typeof TooltipPrimitive.Content> | null>;
}) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      'bg-popover text-popover-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[70] overflow-hidden rounded-md border px-3 py-1.5 text-sm shadow-md',
      className
    )}
    {...props}
  />
);
TooltipContent.displayName =
  TooltipPrimitive.Content.displayName || 'TooltipContent';

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipPortal,
  TooltipProvider
};
